'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  IconBuildingStore,
  IconCalendarEvent,
  IconCalendarOff,
  IconHistory,
  IconNote,
  IconMail,
  IconPhone,
  IconUser,
  IconMapPin,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CancelOrderScheduleRequestBody,
  cancelOrderScheduleRequestBody,
  FetchOrderScheduleResponseBody,
  InsuredParty,
  OrderScheduleBlockedReason,
  OrderScheduleBranch,
  OrderScheduleEntry,
  SetOrderScheduleRequestBody,
} from 'bff';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import {
  ORDER_SCHEDULING_NOTE_MAX_LENGTH,
  orderScheduleFloor,
} from 'piramid-constants';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { buildGoogleMapsUrl, formatPlaceAddress } from 'utils';
import { WretchError } from 'wretch/resolver';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from 'ui';
import { z } from 'zod';
import { useApiError } from '../hooks/useApiError';
import { LocationMap } from './LocationMap';
import { buildOrderProgressQueryKey } from './OrderProgressHeader';

export const buildOrderScheduleQueryKey = (orderId: string) => [
  `order-schedule-${orderId}`,
];

const formatDateTime = (value: string) =>
  format(new Date(value), 'dd/MM/yyyy HH:mm');

// Coordination is agreed in 5-minute slots; the native picker steps by it.
const SCHEDULE_TIME_STEP_SECONDS = 300;

const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

// `new Date('yyyy-MM-dd')` parses as UTC and lands a day early west of
// Greenwich, and the picker's value is a local calendar date.
const parseDateInput = (value: string) => {
  if (!value) return undefined;

  const [year, month, day] = value.split('-').map(Number);

  return new Date(year, month - 1, day);
};

/**
 * Date and time are captured separately and combined into one local instant sent
 * as UTC ISO 8601. Storing the time is what keeps a bare date from shifting.
 */
const scheduleFormSchema = z.object({
  date: z.date({
    required_error: 'Elegí una fecha',
    invalid_type_error: 'Elegí una fecha',
  }),
  time: z.string().regex(/^\d{2}:\d{2}$/, 'Elegí una hora'),
  branch_id: z.string().min(1, 'Elegí una sucursal'),
  note: z.string().max(ORDER_SCHEDULING_NOTE_MAX_LENGTH).optional(),
  notify_insured: z.boolean(),
});

type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

type CancelFormValues = CancelOrderScheduleRequestBody;

// The map is a visual aid inside an already tall dialog, so it stays short.
const BRANCH_MAP_HEIGHT = '10rem';

const combineDateAndTime = ({ date, time }: ScheduleFormValues) => {
  const [hours, minutes] = time.split(':').map(Number);
  const combined = new Date(date);

  combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);

  return combined;
};

const InsuredContact = ({ insured }: { insured: InsuredParty }) => {
  const t = useTranslations('orders.scheduling');

  const hasData = Object.values(insured).some(Boolean);

  return (
    <div className='flex flex-col gap-2 rounded-xl border p-4'>
      <h4 className='text-sm font-semibold'>{t('insured.title')}</h4>

      {hasData ? (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-2 text-sm'>
          {insured.name && (
            <span className='flex items-center gap-2'>
              <IconUser className='h-3.5 w-3.5 text-muted-foreground' />
              {insured.name}
              {insured.identification &&
                ` (DNI/CUIT: ${insured.identification})`}
            </span>
          )}
          {insured.phone && (
            <a
              className='flex w-fit items-center gap-2 hover:underline'
              href={`tel:${insured.phone}`}
            >
              <IconPhone className='h-3.5 w-3.5 text-muted-foreground' />
              {insured.phone}
            </a>
          )}
          {insured.email && (
            <a
              className='flex w-fit items-center gap-2 hover:underline'
              href={`mailto:${insured.email}`}
            >
              <IconMail className='h-3.5 w-3.5 text-muted-foreground' />
              {insured.email}
            </a>
          )}
        </div>
      ) : (
        <span className='text-sm text-muted-foreground'>
          {t('insured.empty')}
        </span>
      )}
    </div>
  );
};

/** Where the claimant was told to go, with a way to actually get there. */
const ScheduledBranch = ({
  branch,
  providerName,
}: {
  branch: OrderScheduleBranch;
  providerName: string;
}) => {
  const t = useTranslations('orders.scheduling');

  return (
    <div className='flex flex-col gap-1'>
      <span className='flex items-center gap-1 text-sm font-medium'>
        <IconBuildingStore className='h-4 w-4 text-muted-foreground' />
        {branch.name} ({providerName})
      </span>
      <span className='pl-5 text-sm text-muted-foreground flex items-center gap-1'>
        {formatPlaceAddress(branch)}
        <a
          className='w-fit pl-5 text-sm underline flex items-center gap-1'
          href={buildGoogleMapsUrl(branch)}
          rel='noreferrer'
          target='_blank'
        >
          <IconMapPin className='h-3.5 w-3.5 text-muted-foreground' />
          {t('branch.directions')}
        </a>
      </span>
    </div>
  );
};

const actorName = (actor: OrderScheduleEntry['created_by']) =>
  [actor?.first_name, actor?.last_name].filter(Boolean).join(' ') ||
  actor?.email;

// Read from the coordination's own status, never inferred from which instants
// are set: `superseded` and `canceled` are different facts about the same row.
const ScheduleHistoryEntry = ({
  entry,
  providerName,
}: {
  entry: OrderScheduleEntry;
  providerName: string;
}) => {
  const t = useTranslations('orders.scheduling');

  return (
    <li className='flex flex-col border-l-2 pl-3 text-sm'>
      <span>
        {t(`history.status.${entry.status}`, {
          at: formatDateTime(entry.scheduled_for),
        })}
      </span>
      <span className='text-muted-foreground'>
        {t('history.atBranch', {
          branch: entry.branch.name,
          provider: providerName,
        })}
      </span>
      {entry.note && (
        <span className='text-muted-foreground '>«{entry.note}»</span>
      )}
      <span className='text-xs text-muted-foreground'>
        {actorName(entry.created_by)} — {formatDateTime(entry.created_at)}
        {entry.notified_insured && ` · ${t('history.insuredNotified')}`}
      </span>

      {/* The void is part of the same row, so who called it off and why read
          right under the appointment they undid. */}
      {entry.status === 'canceled' && (
        <>
          {entry.canceled_reason && (
            <span className='text-muted-foreground '>
              «{entry.canceled_reason}»
            </span>
          )}
          <span className='text-xs text-muted-foreground'>
            {t('history.canceledBy', {
              actor: actorName(entry.canceled_by) ?? '',
              at: formatDateTime(entry.canceled_at!),
            })}
          </span>
        </>
      )}
    </li>
  );
};

const ScheduleHistory = ({
  history,
  providerName,
}: {
  history: OrderScheduleEntry[];
  providerName: string;
}) => {
  const t = useTranslations('orders.scheduling');

  if (!history.length) return null;

  return (
    <div className='flex flex-col gap-2 rounded-xl border p-4'>
      <h4 className='flex items-center gap-2 text-sm font-semibold'>
        <IconHistory className='h-4 w-4' />
        {t('history.title')}
      </h4>

      <ul className='flex flex-col gap-2'>
        {history.map((entry) => (
          <ScheduleHistoryEntry
            entry={entry}
            key={entry.id}
            providerName={providerName}
          />
        ))}
      </ul>
    </div>
  );
};

/**
 * order_scheduling — Scheduling tab panel, shared by insurer-app and
 * provider-app.
 *
 * Edit capability is expressed by INJECTING `saveSchedule`: no fetcher, no form.
 * Data comes from `useQuery` seeded with SSR `initialData`, never frozen props.
 */
export const OrderSchedulePanel = ({
  orderId,
  initialData,
  fetchSchedule,
  saveSchedule,
  cancelSchedule,
}: {
  orderId: string;
  initialData?: FetchOrderScheduleResponseBody;
  fetchSchedule: () => Promise<FetchOrderScheduleResponseBody>;
  saveSchedule?: (body: SetOrderScheduleRequestBody) => Promise<unknown>;
  cancelSchedule?: (body: CancelOrderScheduleRequestBody) => Promise<unknown>;
}) => {
  const t = useTranslations('orders.scheduling');
  const { toast } = useToast();
  const { handleError } = useApiError();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const query = useQuery({
    queryKey: buildOrderScheduleQueryKey(orderId),
    queryFn: fetchSchedule,
    initialData,
  });

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
  });

  const cancelForm = useForm<CancelFormValues>({
    resolver: zodResolver(cancelOrderScheduleRequestBody),
    defaultValues: { reason: '' },
  });

  const save = useMutation({
    mutationFn: (values: ScheduleFormValues) =>
      saveSchedule!({
        scheduled_for: combineDateAndTime(values),
        branch_id: values.branch_id,
        note: values.note?.trim() || null,
        notify_insured: values.notify_insured,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(buildOrderScheduleQueryKey(orderId));
      // The header derives the stage and the completion gate from this.
      queryClient.invalidateQueries(buildOrderProgressQueryKey(orderId));
      setEditing(false);
      toast({ title: t('saveSuccess') });
    },
    onError: (error) => handleError(error as WretchError),
  });

  const cancel = useMutation({
    mutationFn: (values: CancelFormValues) => cancelSchedule!(values),
    onSuccess: () => {
      queryClient.invalidateQueries(buildOrderScheduleQueryKey(orderId));
      // Voiding re-closes the completion gate, which the header derives.
      queryClient.invalidateQueries(buildOrderProgressQueryKey(orderId));
      setCanceling(false);
      toast({ title: t('cancelSuccess') });
    },
    onError: (error) => handleError(error as WretchError),
  });

  const data = query.data ?? initialData;

  // A claimant with no email on file cannot be notified, so the checkbox says
  // so instead of promising something that will not happen.
  const insuredEmail = data?.insured?.email ?? null;

  const openEditor = useCallback(() => {
    const current = data?.current ? new Date(data.current.scheduled_for) : null;
    // Falls back to the central branch: the provider's main address is the
    // likeliest place, and it saves a click on the common case.
    const central = data?.branch_options.find((branch) => branch.is_central);

    form.reset({
      date: current ?? undefined,
      time: current ? format(current, 'HH:mm') : '',
      branch_id: data?.current?.branch.id ?? central?.id ?? '',
      note: data?.current?.note ?? '',
      notify_insured: !!data?.insured?.email,
    });
    setEditing(true);
  }, [data, form]);

  const submit = useCallback(
    (values: ScheduleFormValues) => save.mutate(values),
    [save],
  );

  const openCanceler = useCallback(() => {
    cancelForm.reset({ reason: '' });
    setCanceling(true);
  }, [cancelForm]);

  const submitCancel = useCallback(
    (values: CancelFormValues) => cancel.mutate(values),
    [cancel],
  );

  // No upper bound: scheduling months ahead is legitimate. The floor is the very
  // rule the backend validates.
  const minScheduleDate = useMemo(
    () => format(orderScheduleFloor(new Date()), DATE_INPUT_FORMAT),
    [],
  );

  const blockedHint = useMemo(() => {
    if (!data?.blocked_reason) return null;

    return t(
      `blocked.${data.blocked_reason satisfies OrderScheduleBlockedReason}`,
    );
  }, [data, t]);

  const cancelBlockedHint = useMemo(() => {
    if (!data?.cancel_blocked_reason) return null;

    return t(
      `blocked.${
        data.cancel_blocked_reason satisfies OrderScheduleBlockedReason
      }`,
    );
  }, [data, t]);

  // Watched, not read from the ref, so the map follows the picker.
  const selectedBranchId = form.watch('branch_id');

  const selectedBranch = useMemo(
    () =>
      data?.branch_options.find((branch) => branch.id === selectedBranchId) ??
      null,
    [data, selectedBranchId],
  );

  if (!data) return null;

  const current = data.current;

  return (
    <div className='mx-2 flex flex-col gap-3 bg-white'>
      <div className='flex flex-col gap-3 rounded-xl border p-4'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            {current ? (
              <IconCalendarEvent className='h-5 w-5' />
            ) : (
              <IconCalendarOff className='h-5 w-5' />
            )}
            <Badge variant={current ? 'success' : 'neutral'}>
              {t(current ? 'status.scheduled' : 'status.pending')}
            </Badge>
          </div>

          <div className='flex items-center gap-2'>
            {/* Only offered once there IS an appointment: with none there is
                nothing to withdraw. */}
            {cancelSchedule && current && (
              <Button
                disabled={!data.can_cancel}
                onClick={openCanceler}
                size='sm'
                className='h-8'
                title={cancelBlockedHint ?? undefined}
                variant='ghost'
              >
                {t('action.cancel')}
              </Button>
            )}

            {saveSchedule && (
              <Button
                disabled={!data.can_edit}
                onClick={openEditor}
                size='sm'
                className='h-8'
                title={blockedHint ?? undefined}
                variant='outline'
              >
                {t(current ? 'action.edit' : 'action.create')}
              </Button>
            )}
          </div>
        </div>

        {current ? (
          <div className='flex flex-col gap-1'>
            <span className='text-xl font-semibold'>
              {formatDateTime(current.scheduled_for)}
            </span>
            <ScheduledBranch
              branch={current.branch}
              providerName={data.provider_name}
            />
            {current.note && (
              <div className='flex items-center gap-1 text-sm'>
                <IconNote className='h-4 w-4 text-muted-foreground' />
                <span className='text-sm '>«{current.note}»</span>
              </div>
            )}
            {current.created_by && (
              <span className='text-xs text-muted-foreground'>
                {t('recordedBy', {
                  actor: actorName(current.created_by) ?? '',
                  at: formatDateTime(current.created_at),
                })}
              </span>
            )}
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {blockedHint ?? t('empty')}
          </p>
        )}
      </div>

      {data.insured && <InsuredContact insured={data.insured} />}

      <ScheduleHistory
        history={data.history}
        providerName={data.provider_name}
      />

      <Dialog onOpenChange={setEditing} open={editing}>
        {/* The branch picker and its map make this form taller than the
            viewport on a laptop, and the dialog does not scroll on its own.
            Wider than the default so the branch line and the map are legible,
            never wider than the phone it is read on. */}
        <DialogContent className='max-h-[90vh] w-11/12 max-w-xl overflow-y-auto overflow-x-hidden p-4 sm:p-6'>
          <DialogHeader>
            <DialogTitle>
              {t(current ? 'action.edit' : 'action.create')}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              className='flex min-w-0 flex-col gap-4'
              onSubmit={form.handleSubmit(submit)}
            >
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='date'
                  render={({ field }) => (
                    <FormItem className='min-w-0'>
                      <FormLabel>{t('form.date')}</FormLabel>
                      <FormControl>
                        <Input
                          min={minScheduleDate}
                          name={field.name}
                          onBlur={field.onBlur}
                          onChange={(event) =>
                            field.onChange(parseDateInput(event.target.value))
                          }
                          ref={field.ref}
                          type='date'
                          value={
                            field.value
                              ? format(field.value, DATE_INPUT_FORMAT)
                              : ''
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='time'
                  render={({ field }) => (
                    <FormItem className='min-w-0'>
                      <FormLabel>{t('form.time')}</FormLabel>
                      <FormControl>
                        <Input
                          step={SCHEDULE_TIME_STEP_SECONDS}
                          type='time'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name='branch_id'
                render={({ field }) => (
                  <FormItem className='min-w-0'>
                    <FormLabel>{t('form.branch')}</FormLabel>
                    <FormControl>
                      {/* Controlled: `defaultValue` is read once at mount, so
                          a reset would leave the trigger showing a stale pick. */}
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('form.branchPlaceholder')}
                          />
                        </SelectTrigger>
                        <SelectContent className='max-w-[--radix-select-trigger-width]'>
                          {data.branch_options.map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>
                              <span className='block truncate'>
                                {branch.name} — {formatPlaceAddress(branch)}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedBranch && (
                <LocationMap
                  className='w-full min-w-0'
                  height={BRANCH_MAP_HEIGHT}
                  points={[
                    {
                      id: selectedBranch.id,
                      latitude: selectedBranch.latitude,
                      longitude: selectedBranch.longitude,
                      label: selectedBranch.name,
                    },
                  ]}
                />
              )}

              <FormField
                control={form.control}
                name='note'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.note')}</FormLabel>
                    <FormControl>
                      <Textarea
                        maxLength={ORDER_SCHEDULING_NOTE_MAX_LENGTH}
                        placeholder={t('form.notePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='notify_insured'
                render={({ field }) => (
                  <FormItem className='flex min-w-0 flex-row items-center gap-2 mb-2'>
                    <FormControl>
                      {/* Always on for now: every coordination tells the
                          claimant. The value still travels as data so enabling
                          the choice later is a UI-only change. */}
                      <Checkbox
                        className='mt-2 shrink-0'
                        checked={field.value}
                        disabled
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className='font-normal mt-0 min-w-0 break-words'>
                      {insuredEmail
                        ? t('form.notifyInsured', { email: insuredEmail })
                        : t('form.notifyInsuredNoEmail')}
                    </FormLabel>
                  </FormItem>
                )}
              />

              <Button disabled={save.isLoading} type='submit'>
                {t('form.submit')}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* A Dialog and not an AlertDialog: the reason is required, so this needs
          a real form. The counterpart finds out by notification and the reason
          is the only thing telling them what to do next. */}
      <Dialog onOpenChange={setCanceling} open={canceling}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cancel.title')}</DialogTitle>
            <DialogDescription>
              {current
                ? t('cancel.description', {
                    at: formatDateTime(current.scheduled_for),
                  })
                : null}
            </DialogDescription>
          </DialogHeader>

          <Form {...cancelForm}>
            <form
              className='flex flex-col gap-4'
              onSubmit={cancelForm.handleSubmit(submitCancel)}
            >
              <FormField
                control={cancelForm.control}
                name='reason'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('cancel.reason')}</FormLabel>
                    <FormControl>
                      <Textarea
                        maxLength={ORDER_SCHEDULING_NOTE_MAX_LENGTH}
                        placeholder={t('cancel.reasonPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={cancel.isLoading}
                type='submit'
                variant='destructive'
              >
                {t('cancel.confirm')}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
